import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';

function dashboardPathForUser(user) {
  const role = user?.role || 'admin';
  const perms = typeof user?.permissions === 'string'
    ? (() => { try { return JSON.parse(user.permissions); } catch { return {}; } })()
    : (user?.permissions || {});
  const hasSuperAccess = user?.isSuperadminPanel || perms.superadmin_access === true;
  if (role === 'superadmin') return '/superadmin/clients';
  if (hasSuperAccess) return '/superadmin/dashboard';
  if (role === 'channel_partner') return '/partner/clients';
  return '/dashboard';
}

function safeRedirectPath(path) {
  if (!path || typeof path !== 'string') return null;
  const p = path.trim();
  if (!p.startsWith('/') || p.startsWith('//')) return null;
  return p;
}

/**
 * Exchanges a one-time impersonation code, stores session in sessionStorage,
 * and opens the selected client's workspace (admin dashboard) directly.
 */
const ImpersonateRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get('code');
    const orgIdFromUrl = searchParams.get('orgId');
    const redirectAfter = safeRedirectPath(
      searchParams.get('redirect') || searchParams.get('next')
    );

    if (!code) {
      navigate('/', { replace: true });
      return;
    }

    const finishWithUser = (token, user) => {
      sessionStorage.setItem('impersonate_token', token);
      sessionStorage.setItem('impersonate_user', JSON.stringify(user));
      sessionStorage.removeItem('selection_data');
      navigate(redirectAfter || dashboardPathForUser(user), { replace: true });
    };

    api.post('/auth/impersonate/exchange', { code })
      .then((res) => {
        if (!res.data?.success) {
          navigate('/', { replace: true });
          return;
        }

        const {
          token,
          user,
          organizations,
          canCreateOrg,
          targetOrgId,
        } = res.data;

        // If a specific deep-link redirect is requested (e.g. /payroll-inspection), go directly there
        if (redirectAfter && (targetOrgId ?? orgIdFromUrl)) {
          const userWithOrg = {
            ...user,
            orgAccountId: user?.orgAccountId ?? Number(targetOrgId ?? orgIdFromUrl),
          };
          finishWithUser(token, userWithOrg);
          return;
        }

        // Always store impersonate token and user
        sessionStorage.setItem('impersonate_token', token);
        sessionStorage.setItem('impersonate_user', JSON.stringify(user));

        // If client has child companies / multiple organizations, show the selection screen
        if (organizations && organizations.length > 1) {
          sessionStorage.setItem('selection_data', JSON.stringify({
            phone: user.phone,
            organizations,
            canCreateOrg: !!canCreateOrg,
          }));
          navigate('/home', { replace: true });
          return;
        }

        const orgId = targetOrgId ?? orgIdFromUrl;

        if (orgId) {
          const userWithOrg = {
            ...user,
            orgAccountId: user?.orgAccountId ?? Number(orgId),
          };
          finishWithUser(token, userWithOrg);
          return;
        }

        if (organizations?.length) {
          sessionStorage.setItem('selection_data', JSON.stringify({
            phone: user.phone,
            organizations,
            canCreateOrg: !!canCreateOrg,
          }));
        }

        navigate('/home', { replace: true });
      })
      .catch(() => {
        navigate('/', { replace: true });
      });
  }, [searchParams, navigate]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <p>Opening client workspace...</p>
    </div>
  );
};

export default ImpersonateRedirect;
